<template>
  <el-dialog title="操作测试" :visible.sync="visible" width="1180px" append-to-body
    :close-on-click-modal="!running" :close-on-press-escape="!running" @close="handleClose">
    <el-form size="mini" :inline="true" class="ops-form">
      <el-form-item label="模式">
        <el-radio-group v-model="form.concurrent" :disabled="running">
          <el-radio :label="false">顺序</el-radio>
          <el-radio :label="true">并发</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="种单">
        <el-input-number v-model="form.seed" :min="1" :max="40" :controls="false" style="width: 56px"
          :disabled="running"></el-input-number>
      </el-form-item>
      <el-form-item label="混合次数">
        <el-input-number v-model="form.batch" :min="1" :max="200" :controls="false" style="width: 64px"
          :disabled="running"></el-input-number>
      </el-form-item>
      <el-form-item v-if="form.concurrent" label="线程">
        <el-input-number v-model="form.threads" :min="1" :max="16" :controls="false" style="width: 56px"
          :disabled="running"></el-input-number>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" size="mini" :loading="running" @click="start">开始测试</el-button>
      </el-form-item>
    </el-form>

    <div class="hint">
      乘客走下单/改签/取消确认链路；后台走派单、撤回、改签。结束后巡检行程链与座次。
    </div>

    <div v-if="task" class="progress-row">
      <span class="phase">{{ phaseText }}</span>
      <el-progress :percentage="progressPercent" :stroke-width="8" :status="progressStatus"
        class="bar"></el-progress>
      <span class="pct">{{ task.current || 0 }}/{{ task.total || 0 }}</span>
      <span v-if="elapsedText" class="elapsed">{{ elapsedText }}</span>
    </div>

    <div v-if="task && task.errorMsg" class="error">{{ task.errorMsg }}</div>

    <el-table v-if="resultOps.length" :data="resultOps" size="mini" border class="ops-table">
      <el-table-column prop="label" label="操作" width="110"></el-table-column>
      <el-table-column prop="attempt" label="尝试" width="60" align="right"></el-table-column>
      <el-table-column prop="ok" label="成功" width="60" align="right"></el-table-column>
      <el-table-column prop="skip" label="跳过" width="60" align="right"></el-table-column>
      <el-table-column prop="fail" label="失败" width="60" align="right"></el-table-column>
      <el-table-column label="成功率" width="72" align="right">
        <template slot-scope="scope">{{ successRateText(scope.row) }}</template>
      </el-table-column>
      <el-table-column label="成功明细" min-width="220">
        <template slot-scope="scope">
          <div v-if="!scope.row.okReasons || !scope.row.okReasons.length" class="muted">—</div>
          <div v-for="r in scope.row.okReasons" :key="'o-' + r" class="reason ok">{{ r }}</div>
        </template>
      </el-table-column>
      <el-table-column label="跳过原因" min-width="220">
        <template slot-scope="scope">
          <div v-if="!scope.row.skipReasons || !scope.row.skipReasons.length" class="muted">—</div>
          <div v-for="r in scope.row.skipReasons" :key="'s-' + r" class="reason skip">{{ r }}</div>
        </template>
      </el-table-column>
      <el-table-column label="失败原因" min-width="220">
        <template slot-scope="scope">
          <div v-if="!scope.row.failReasons || !scope.row.failReasons.length" class="muted">—</div>
          <div v-for="r in scope.row.failReasons" :key="'f-' + r" class="reason fail">{{ r }}</div>
        </template>
      </el-table-column>
    </el-table>

    <pre v-if="unexpectedText" class="unexpected">{{ unexpectedText }}</pre>

    <div v-if="result" class="summary">
      种单 {{ result.created }} · 在册 {{ result.live }} · 行程 {{ result.routes }}
      · 成功 {{ result.ok }} / 跳过 {{ result.skip }} / 失败 {{ result.fail }}
      · 总成功率 {{ overallSuccessRate }}
      · 耗时 {{ elapsedText }}
      · 巡检
      <span :class="result.inspectOk ? 'ok' : 'bad'">{{ result.inspectOk ? "通过" : "未通过" }}</span>
      （{{ result.planCount }} 个规划 / {{ result.driverCount }} 名司机）
    </div>

    <pre v-if="result && result.inspectText" class="inspect">{{ result.inspectText }}</pre>
  </el-dialog>
</template>

<script>
import { submitEvOpsSimulate, getEvOpsSimulateTask } from "@/api/ev_solver";

export default {
  name: "EvOpsTestDialog",
  data() {
    return {
      visible: false,
      running: false,
      form: {
        concurrent: false,
        seed: 12,
        batch: 80,
        threads: 8
      },
      taskId: null,
      task: null,
      pollTimer: null
    };
  },
  computed: {
    result() {
      return this.task && this.task.result ? this.task.result : null;
    },
    resultOps() {
      return (this.result && this.result.ops) || [];
    },
    unexpectedText() {
      const list = this.result && this.result.unexpected ? this.result.unexpected : [];
      if (!list.length) {
        return "";
      }
      return "非预期异常：\n" + list.slice(0, 20).join("\n");
    },
    progressPercent() {
      if (!this.task || !this.task.total) {
        return 0;
      }
      return Math.min(100, Math.round((this.task.current / this.task.total) * 100));
    },
    progressStatus() {
      if (!this.task) {
        return null;
      }
      if (this.task.status === 2) {
        return "exception";
      }
      if (this.task.status === 1) {
        return this.result && this.result.inspectOk && this.result.fail === 0 ? "success" : "warning";
      }
      return null;
    },
    phaseText() {
      if (!this.task) {
        return "";
      }
      return this.task.phase || (this.task.concurrent ? "并发测试" : "顺序测试");
    },
    elapsedMs() {
      if (this.result && this.result.elapsedMs) {
        return this.result.elapsedMs;
      }
      if (this.task && this.task.elapsedMs) {
        return this.task.elapsedMs;
      }
      if (this.task && this.task.ctime) {
        const start = Date.parse(String(this.task.ctime).replace(/-/g, "/"));
        if (!isNaN(start)) {
          const endSrc = this.task.completeTime || Date.now();
          const end = typeof endSrc === "number" ? endSrc : Date.parse(String(endSrc).replace(/-/g, "/"));
          if (!isNaN(end)) {
            return Math.max(0, end - start);
          }
        }
      }
      return 0;
    },
    elapsedText() {
      return this.formatElapsed(this.elapsedMs);
    },
    overallSuccessRate() {
      if (!this.result) {
        return "—";
      }
      return this.successRateText({
        attempt: this.result.ok + this.result.skip + this.result.fail,
        ok: this.result.ok
      });
    }
  },
  beforeDestroy() {
    this.stopPoll();
  },
  methods: {
    open() {
      this.visible = true;
    },
    startIfIdle() {
      if (this.running) {
        return false;
      }
      this.start();
      return true;
    },
    successRateText(row) {
      const attempt = row && row.attempt ? row.attempt : 0;
      if (!attempt) {
        return "—";
      }
      const ok = row.ok || 0;
      const rate = (ok / attempt) * 100;
      const text = Number.isInteger(rate) ? String(rate) : rate.toFixed(1);
      return `${text}%`;
    },
    formatElapsed(ms) {
      if (!ms || ms <= 0) {
        return "";
      }
      if (ms < 1000) {
        return ms + " ms";
      }
      const sec = ms / 1000;
      if (sec < 60) {
        return (Number.isInteger(sec) ? sec : sec.toFixed(1)) + " 秒";
      }
      const min = Math.floor(sec / 60);
      const remain = (sec - min * 60).toFixed(1);
      return min + " 分 " + remain + " 秒";
    },
    start() {
      this.stopPoll();
      this.running = true;
      this.task = null;
      this.taskId = null;
      submitEvOpsSimulate({
        concurrent: this.form.concurrent,
        seed: this.form.seed,
        batch: this.form.batch,
        threads: this.form.threads
      })
        .then((res) => {
          this.task = res.data;
          this.taskId = res.data && res.data.id;
          this.pollTask();
          this.pollTimer = setInterval(() => this.pollTask(), 2000);
        })
        .catch(() => {
          this.running = false;
        });
    },
    pollTask() {
      if (!this.taskId) {
        return;
      }
      getEvOpsSimulateTask(this.taskId).then((res) => {
        this.task = res.data;
        if (!this.task) {
          return;
        }
        if (this.task.status === 1) {
          this.running = false;
          this.stopPoll();
          const inspectOk = this.result && this.result.inspectOk;
          const fail = this.result ? this.result.fail : 0;
          if (inspectOk && fail === 0) {
            this.$message.success("操作测试完成，行程巡检通过");
          } else if (inspectOk) {
            this.$message.warning("测试结束，巡检通过，但有失败操作");
          } else {
            this.$message.error("测试结束，行程巡检未通过");
          }
          this.$emit("done");
        } else if (this.task.status === 2) {
          this.running = false;
          this.stopPoll();
          this.$message.error(this.task.errorMsg || "操作测试失败");
          this.$emit("done");
        }
      });
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    handleClose() {
      if (this.running) {
        return;
      }
      this.stopPoll();
    }
  }
};
</script>

<style lang="less" scoped>
.ops-form {
  /deep/ .el-form-item {
    margin-bottom: 8px;
    margin-right: 12px;
  }
}

.hint {
  font-size: 12px;
  color: #909399;
  margin-bottom: 10px;
  line-height: 1.5;
}

.progress-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.phase {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
}

.bar {
  flex: 1;
}

.pct {
  font-size: 12px;
  color: #606266;
  min-width: 64px;
  text-align: right;
}

.elapsed {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
}

.error {
  color: #f56c6c;
  font-size: 12px;
  margin-bottom: 8px;
  white-space: pre-wrap;
}

.ops-table {
  margin-top: 4px;
}

.muted {
  color: #c0c4cc;
}

.reason {
  font-size: 12px;
  line-height: 1.4;
  word-break: break-all;
}

.reason.ok {
  color: #67c23a;
}

.reason.skip {
  color: #909399;
}

.reason.fail {
  color: #f56c6c;
}

.unexpected {
  margin: 8px 0 0;
  max-height: 120px;
  overflow: auto;
  padding: 8px 10px;
  background: #fef0f0;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #f56c6c;
  white-space: pre-wrap;
  word-break: break-all;
}

.summary {
  margin-top: 10px;
  font-size: 12px;
  color: #606266;
  line-height: 1.6;
}

.ok {
  color: #67c23a;
  font-weight: 600;
}

.bad {
  color: #f56c6c;
  font-weight: 600;
}

.inspect {
  margin: 8px 0 0;
  max-height: 220px;
  overflow: auto;
  padding: 8px 10px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #303133;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
